// ResultsGrid.tsx
import ColourSwatchVariantEnum from '@models/enums/ColourSwatchVariantEnum';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IWishlistDrawerLabels } from '@models/IWishlistDrawer';
import ListingPlaceholder from '@molecules/ListingPlaceholder/ListingPlaceholder';
import { fadeInUpAnimation } from '@utility/config/animations';
import { useListingState } from '@utility/context/ListingContext';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, ReactNode } from 'react';
import { renderCard, stableKey } from './ListingCardFactory';

interface Props {
  readonly applyPendingGridSize: () => void;
  readonly cardVariant: ColourSwatchVariantEnum;
  readonly errors?: any;
  readonly featuredHotspotCard?: ReactNode;
  readonly featuredInspirationCard?: ReactNode;
  readonly freetext?: string;
  readonly gridClasses: string;
  readonly gridSize: number;
  readonly hasPopulated: boolean;
  readonly hotspotIndex?: number;
  readonly inspirationIndex?: number;
  readonly isAnimatingGridChange: boolean;
  readonly loading: boolean;
  readonly noResultsHeader: string;
  readonly noResultsText: string;
  readonly page: number;
  readonly positionClass: string;
  readonly site: SiteEnum;
  readonly hasMoreItemsToShow?: boolean;
  readonly type: ListingTypeEnum;
  readonly wishlistDrawerLabels?: IWishlistDrawerLabels;
  readonly onLoadMore?: () => void;
}

const ListingResultsGrid = ({
  hasMoreItemsToShow,
  applyPendingGridSize,
  cardVariant,
  errors,
  featuredHotspotCard,
  featuredInspirationCard,
  freetext,
  gridClasses,
  gridSize,
  hasPopulated,
  hotspotIndex,
  inspirationIndex,
  isAnimatingGridChange,
  loading,
  noResultsHeader,
  noResultsText,
  page,
  positionClass,
  site,
  type,
  wishlistDrawerLabels,
  onLoadMore,
}: Props) => {
  const { slicedListItems: items } = useListingState();

  return (
    <div
      className={classNames(
        'tw-transition-ease tw-container tw-relative tw-grid tw-grid-cols-12 tw-items-center',
        gridClasses
      )}
    >
      <AnimatePresence mode="wait" onExitComplete={applyPendingGridSize}>
        {!isAnimatingGridChange && (!loading || page !== 0) && (
          <>
            {!items?.length && hasPopulated && (
              <motion.div
                key="no-results"
                variants={fadeInUpAnimation}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="tw-absolute tw-inset-0 tw-flex tw-h-[400px] tw-items-center"
              >
                <ListingPlaceholder
                  title={noResultsHeader.replace('{0}', freetext ?? '')}
                  message={noResultsText}
                />
              </motion.div>
            )}

            {!!items?.length &&
              items.map((item, index) => {
                const key = stableKey(item, type, gridSize);

                const itemObject =
                  type === ListingTypeEnum.Inspiration
                    ? {
                        ...item,
                        cardIndex: index,
                        errors,
                        hasMoreItemsToShow,
                        inspirationOverlayItems: items,
                        loading,
                        onRequestMore: onLoadMore,
                      }
                    : item;

                return (
                  <Fragment key={key}>
                    <motion.div
                      variants={fadeInUpAnimation}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className={classNames('tw-h-full', positionClass)}
                    >
                      {renderCard({
                        item: itemObject,
                        type,
                        site,
                        gridSize,
                        cardVariant,
                        wishlistDrawerLabels,
                      })}
                    </motion.div>
                    {index === hotspotIndex && featuredHotspotCard}
                    {index === inspirationIndex && featuredInspirationCard}
                  </Fragment>
                );
              })}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ListingResultsGrid;
